import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import {
  loadUser,
  loadUserSuccess,
  loadUserFailure,
  freezeAccount,
  freezeAccountSuccess,
  freezeAccountFailure,
  emergencyStopAccount,
  emergencyStopAccountSuccess,
  emergencyStopAccountFailure,
  killSwitchAccount,
  killSwitchAccountSuccess,
  killSwitchAccountFailure,
  loadRoles,
  loadRolesSuccess,
  loadRolesFailure,
  loadOwnUnfreezeRequest,
  loadOwnUnfreezeRequestSuccess,
  loadOwnUnfreezeRequestFailure,
  requestOwnUnfreeze,
  requestOwnUnfreezeSuccess,
  requestOwnUnfreezeFailure,
} from './dashboard.actions';
import { catchError, map, switchMap } from 'rxjs/operators';
import { of, defer } from 'rxjs';
import { DashboardService } from '../services/dashboard.service';
import { EmergencyStopResponse } from '../models/dashboard.model';

@Injectable()
export class DashboardEffects {
  constructor(
    private actions$: Actions,
    private dashboardService: DashboardService
  ) {}

  loadUser$ = createEffect(() =>
    defer(() =>
      this.actions$.pipe(
        ofType(loadUser),
        switchMap(() =>
          defer(() => this.dashboardService.getProfile()).pipe(
            map(res => {
              const user = res?.data;
              if (!user) {
                return loadUserFailure({
                  error: new Error('Empty user profile envelope'),
                });
              }
              return loadUserSuccess({ user });
            }),
            catchError(error => of(loadUserFailure({ error })))
          )
        )
      )
    )
  );

  loadOwnUnfreezeRequest$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadOwnUnfreezeRequest),
      switchMap(() =>
        this.dashboardService.getOwnUnfreezeRequest().pipe(
          map(res =>
            loadOwnUnfreezeRequestSuccess({
              pendingRequestId: res?.data?.pendingRequestId ?? null,
            })
          ),
          catchError(error => of(loadOwnUnfreezeRequestFailure({ error })))
        )
      )
    )
  );

  // The error toast comes from the HTTP interceptor. A failure re-reads the
  // pending request: the usual one is "already pending" (raised by an owner,
  // or from another tab), and the button must then show it as sent.
  requestOwnUnfreeze$ = createEffect(() =>
    this.actions$.pipe(
      ofType(requestOwnUnfreeze),
      switchMap(() =>
        this.dashboardService.requestOwnUnfreeze().pipe(
          map(res =>
            requestOwnUnfreezeSuccess({ requestId: res?.data?.requestId ?? '' })
          ),
          catchError(error =>
            of(requestOwnUnfreezeFailure({ error }), loadOwnUnfreezeRequest())
          )
        )
      )
    )
  );

  freezeAccount$ = createEffect(() =>
    this.actions$.pipe(
      ofType(freezeAccount),
      switchMap(() =>
        this.dashboardService.freezeAccount().pipe(
          switchMap(() => [
            freezeAccountSuccess(),
            loadUser(), // Reload user profile to get updated status
          ]),
          catchError(error => {
            // Extract error message from nested structure
            const errorData = error?.error;
            let errorMessage: {
              message: string;
              userId?: string;
              status?: string;
            };

            if (
              errorData?.message &&
              typeof errorData.message === 'object' &&
              errorData.message.message
            ) {
              // Handle nested message structure
              errorMessage = {
                message: errorData.message.message,
                userId: errorData.message.userId,
                status: errorData.message.status,
              };
            } else if (typeof errorData?.message === 'string') {
              // Handle simple string message
              errorMessage = {
                message: errorData.message,
              };
            } else {
              // Fallback
              errorMessage = {
                message: error?.message || 'خطا: عملیات ناموفق بود',
              };
            }

            return of(freezeAccountFailure({ error: errorMessage }));
          })
        )
      )
    )
  );

  emergencyStopAccount$ = createEffect(() =>
    this.actions$.pipe(
      ofType(emergencyStopAccount),
      switchMap(({ reason }) =>
        this.dashboardService.emergencyStopAccount(reason).pipe(
          switchMap(res => {
            const response = res?.data ?? ({} as EmergencyStopResponse);
            return [
              emergencyStopAccountSuccess({ response }),
              loadUser(), // Reload user profile to get updated status
            ];
          }),
          catchError(error => {
            const errorData = error?.error;
            let errorMessage: {
              message: string;
              userId?: string;
              status?: string;
            };

            if (
              errorData?.message &&
              typeof errorData.message === 'object' &&
              errorData.message.message
            ) {
              errorMessage = {
                message: errorData.message.message,
                userId: errorData.message.userId,
                status: errorData.message.status,
              };
            } else if (typeof errorData?.message === 'string') {
              errorMessage = {
                message: errorData.message,
              };
            } else {
              errorMessage = {
                message: error?.message || 'خطا: عملیات ناموفق بود',
              };
            }

            return of(emergencyStopAccountFailure({ error: errorMessage }));
          })
        )
      )
    )
  );

  killSwitchAccount$ = createEffect(() =>
    this.actions$.pipe(
      ofType(killSwitchAccount),
      switchMap(({ reason, enablePanicMode }) =>
        this.dashboardService.killSwitch(reason, enablePanicMode).pipe(
          switchMap(() => [
            killSwitchAccountSuccess(),
            loadUser(), // Reload user profile to get updated status
          ]),
          catchError(error => {
            const errorData = error?.error;
            let errorMessage: {
              message: string;
              userId?: string;
              status?: string;
            };

            if (
              errorData?.message &&
              typeof errorData.message === 'object' &&
              errorData.message.message
            ) {
              errorMessage = {
                message: errorData.message.message,
                userId: errorData.message.userId,
                status: errorData.message.status,
              };
            } else if (typeof errorData?.message === 'string') {
              errorMessage = {
                message: errorData.message,
              };
            } else {
              errorMessage = {
                message: error?.message || 'خطا: عملیات ناموفق بود',
              };
            }

            return of(killSwitchAccountFailure({ error: errorMessage }));
          })
        )
      )
    )
  );

  loadRoles$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadRoles),
      switchMap(() =>
        this.dashboardService.getRoles().pipe(
          map(res => loadRolesSuccess({ roles: res?.data?.roles ?? [] })),
          catchError(error =>
            of(
              loadRolesFailure({
                error: error?.message || 'Failed to load roles',
              })
            )
          )
        )
      )
    )
  );
}
